<template>
  <div class="container mt-3 pb-4 seguimiento-page">
    <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
      <div>
        <h2 class="h5 mb-0"><i class="bi bi-truck me-1"></i>Seguimiento</h2>
        <p class="small text-muted mb-0">
          Pedidos detectados en el correo. Si pasan {{ diasSinCerteza }} días sin aviso de entrega, hay que reclamarlos.
        </p>
      </div>
      <div class="d-flex gap-2">
        <button type="button" class="btn btn-outline-secondary btn-sm" @click="toggleLogs">
          <i class="bi bi-journal-text me-1"></i>Logs
        </button>
        <button
          type="button"
          class="btn btn-primary btn-sm"
          :disabled="sincronizando || !connected"
          @click="sincronizar"
        >
          <span v-if="sincronizando" class="spinner-border spinner-border-sm me-1"></span>
          <i v-else class="bi bi-arrow-clockwise me-1"></i>
          Actualizar
        </button>
      </div>
    </div>

    <div v-if="error" class="alert alert-danger alert-dismissible py-2">
      {{ error }}
      <button type="button" class="btn-close" @click="error = ''"></button>
    </div>
    <div v-if="mensaje" class="alert alert-success alert-dismissible py-2">
      {{ mensaje }}
      <button type="button" class="btn-close" @click="mensaje = ''"></button>
    </div>

    <div v-if="connected === false" class="alert alert-warning py-2">
      Conecta Gmail en <router-link to="/gmail">Bandeja</router-link> para que el agente archive y siga los pedidos.
    </div>

    <div v-if="logsAbiertos" class="card border-0 shadow-sm mb-3">
      <div class="card-header py-2 d-flex justify-content-between align-items-center">
        <strong>Correos tratados</strong>
        <button type="button" class="btn-close" aria-label="Cerrar logs" @click="logsAbiertos = false"></button>
      </div>
      <div class="table-responsive">
        <table class="table table-sm mb-0 align-middle">
          <thead>
            <tr>
              <th>Cuándo</th>
              <th>Acción</th>
              <th>Correo</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!logs.length">
              <td colspan="3" class="text-muted">Todavía no hay correos tratados.</td>
            </tr>
            <tr v-for="log in logs" :key="log.id">
              <td class="text-nowrap small">{{ formatoFecha(log.created_at) }}</td>
              <td><span class="badge text-bg-light border">{{ etiquetaAccion(log.accion) }}</span></td>
              <td>
                <div class="small">{{ log.asunto || log.detalle }}</div>
                <div class="text-muted" style="font-size: 0.75rem;">{{ log.remitente }} · {{ log.detalle }}</div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="d-flex flex-wrap gap-1 mb-3">
      <button
        v-for="f in filtros"
        :key="f.id"
        type="button"
        class="btn btn-sm"
        :class="filtro === f.id ? 'btn-dark' : 'btn-outline-secondary'"
        @click="filtro = f.id"
      >
        {{ f.label }}
        <span class="badge bg-light text-dark ms-1">{{ f.count }}</span>
      </button>
    </div>

    <div v-if="cargando" class="text-muted small">Cargando pedidos…</div>
    <div v-else-if="!visibles.length" class="alert alert-light border">
      No hay pedidos en este filtro. El agente revisa el correo cada 30 minutos y también al pulsar Actualizar.
    </div>

    <article
      v-for="pedido in visibles"
      :key="pedido.id"
      class="card border-0 shadow-sm mb-3"
      :class="claseTarjeta(pedido)"
    >
      <div class="card-body">
        <div class="d-flex justify-content-between align-items-start gap-2 flex-wrap">
          <div>
            <h3 class="h6 mb-1">
              {{ pedido.tienda }}
              <span v-if="pedido.transportista"> · {{ pedido.transportista }}</span>
            </h3>
            <p v-if="pedido.producto" class="small mb-1 producto-pedido">{{ pedido.producto }}</p>
            <div v-if="!(pedido.estado === 'en_reparto' && pedido.llegaHoy)" class="small text-muted">
              <span v-if="pedido.numero">Pedido {{ pedido.numero }}</span>
              <span v-if="pedido.importe"> · {{ pedido.importe }} €</span>
            </div>
          </div>
          <span class="badge" :class="claseEstado(pedido)">{{ etiquetaEstado(pedido) }}</span>
        </div>

        <p v-if="pedido.reclamar" class="alert alert-danger py-2 mt-3 mb-2">
          Llevas {{ pedido.diasSinNoticias }} días sin un correo de entrega. Reclámalo a {{ pedido.tienda }}.
          <span v-if="pedido.alertaEnviadaAt" class="d-block small mb-0">Avisado el {{ formatoFecha(pedido.alertaEnviadaAt) }}.</span>
        </p>

        <!-- En reparto: solo el día de entrega, resaltado si es hoy -->
        <template v-if="pedido.estado === 'en_reparto' && !pedido.reclamar">
          <p
            class="entrega-dia mt-3 mb-2"
            :class="pedido.llegaHoy ? 'entrega-hoy' : 'entrega-otro'"
          >
            <i class="bi bi-calendar-event me-1"></i>
            {{ etiquetaEntrega(pedido) }}
          </p>
          <a
            v-if="pedido.trackingUrl"
            class="btn btn-outline-primary btn-sm mb-3"
            :href="pedido.trackingUrl"
            target="_blank"
            rel="noopener noreferrer"
          >
            <i class="bi bi-box-arrow-up-right me-1"></i>
            Ver seguimiento{{ pedido.transportista ? ` en ${pedido.transportista}` : '' }}
          </a>
          <p v-else-if="pedido.tracking" class="small text-muted mb-3">
            Seguimiento {{ pedido.tracking }}
          </p>
        </template>

        <template v-else>
          <p class="small mb-2 mt-2">{{ pedido.asunto }}</p>
          <p class="small text-muted mb-2">Último correo: {{ formatoFecha(pedido.ultimoCorreoAt) }}</p>

          <div v-if="pedido.trackingUrl || pedido.tracking" class="mb-3">
            <a
              v-if="pedido.trackingUrl"
              class="btn btn-outline-primary btn-sm"
              :href="pedido.trackingUrl"
              target="_blank"
              rel="noopener noreferrer"
            >
              <i class="bi bi-box-arrow-up-right me-1"></i>
              Ver seguimiento{{ pedido.transportista ? ` en ${pedido.transportista}` : '' }}
            </a>
            <span v-else class="small text-muted">Seguimiento {{ pedido.tracking }}</span>
          </div>

          <ul v-if="pedido.correos?.length" class="list-unstyled small mb-3">
            <li v-for="(correo, i) in pedido.correos" :key="i" class="mb-1">
              <span class="badge text-bg-light border me-1">{{ etiquetaEstado({ estado: correo.estado }) }}</span>
              {{ correo.asunto }}
              <span class="text-muted">· {{ formatoFecha(correo.fecha) }}</span>
            </li>
          </ul>
        </template>

        <div v-if="pedido.estado !== 'entregado'" class="d-flex gap-2">
          <button type="button" class="btn btn-success btn-sm" :disabled="resolviendo === pedido.id" @click="resolver(pedido, 'entregado')">
            Ya lo he recibido
          </button>
          <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="resolviendo === pedido.id" @click="resolver(pedido, 'descartar')">
            Descartar
          </button>
        </div>
      </div>
    </article>
  </div>
</template>

<script>
import { computed, onMounted, ref } from 'vue';
import axios from '../config/axios';
import { setPedidosReclamarCount } from '../utils/navBadges';

const ETIQUETAS = {
  en_curso: 'En curso',
  en_reparto: 'En reparto',
  entregado: 'Entregado',
  archivado: 'Archivado',
  registrado: 'Registrado',
  actualizado: 'Actualizado',
  alerta: 'Aviso',
  descartar: 'Descartado',
};

export default {
  name: 'SeguimientoPedidos',
  setup() {
    const pedidos = ref([]);
    const logs = ref([]);
    const filtro = ref('activos');
    const logsAbiertos = ref(false);
    const cargando = ref(true);
    const sincronizando = ref(false);
    const resolviendo = ref(0);
    const error = ref('');
    const mensaje = ref('');
    const connected = ref(null);
    const diasSinCerteza = ref(5);

    const llegaHoy = (p) => p.estado === 'en_reparto' && p.llegaHoy;

    const visibles = computed(() => {
      if (filtro.value === 'reclamar') return pedidos.value.filter((p) => p.reclamar);
      if (filtro.value === 'entregado') return pedidos.value.filter((p) => p.estado === 'entregado');
      // En reparto = solo los que llegan hoy
      if (filtro.value === 'en_reparto') return pedidos.value.filter(llegaHoy);
      if (filtro.value === 'en_curso') return pedidos.value.filter((p) => p.estado === 'en_curso');
      return pedidos.value.filter((p) => p.estado !== 'entregado' || p.reclamar);
    });

    const filtros = computed(() => [
      { id: 'activos', label: 'Abiertos', count: pedidos.value.filter((p) => p.estado !== 'entregado').length },
      { id: 'en_curso', label: 'En curso', count: pedidos.value.filter((p) => p.estado === 'en_curso').length },
      { id: 'en_reparto', label: 'En reparto', count: pedidos.value.filter(llegaHoy).length },
      { id: 'reclamar', label: 'Reclamar', count: pedidos.value.filter((p) => p.reclamar).length },
      { id: 'entregado', label: 'Entregados', count: pedidos.value.filter((p) => p.estado === 'entregado').length },
    ]);

    function aplicar(data) {
      pedidos.value = data.pedidos || [];
      connected.value = data.connected !== false;
      diasSinCerteza.value = data.diasSinCerteza || 5;
      setPedidosReclamarCount(data.reclamar || 0);
    }

    async function cargar() {
      cargando.value = true;
      error.value = '';
      try {
        const { data } = await axios.get('/api/gmail/pedidos');
        aplicar(data);
      } catch (e) {
        error.value = e.response?.data?.error || 'No se pudo cargar el seguimiento';
      } finally {
        cargando.value = false;
      }
    }

    async function sincronizar() {
      sincronizando.value = true;
      error.value = '';
      mensaje.value = '';
      try {
        const { data } = await axios.post('/api/gmail/pedidos/sincronizar');
        aplicar(data);
        mensaje.value = `Revisados ${data.revisados || 0}. Nuevos ${data.nuevos || 0}, actualizados ${data.actualizados || 0}, archivados ${data.archivados || 0}.`;
        if (logsAbiertos.value) await cargarLogs();
      } catch (e) {
        error.value = e.response?.data?.error || 'No se pudo revisar el correo';
      } finally {
        sincronizando.value = false;
      }
    }

    async function cargarLogs() {
      const { data } = await axios.get('/api/gmail/pedidos/logs');
      logs.value = data.logs || [];
    }

    async function toggleLogs() {
      logsAbiertos.value = !logsAbiertos.value;
      if (!logsAbiertos.value) return;
      try {
        await cargarLogs();
      } catch (e) {
        error.value = e.response?.data?.error || 'No se pudieron cargar los logs';
      }
    }

    async function resolver(pedido, accion) {
      resolviendo.value = pedido.id;
      error.value = '';
      try {
        await axios.post(`/api/gmail/pedidos/${pedido.id}/resolver`, { accion });
        await cargar();
      } catch (e) {
        error.value = e.response?.data?.error || 'No se pudo actualizar el pedido';
      } finally {
        resolviendo.value = 0;
      }
    }

    function etiquetaEstado(pedido) {
      if (pedido.reclamar) return 'Reclamar';
      return ETIQUETAS[pedido.estado] || pedido.estado;
    }

    function claseEstado(pedido) {
      if (pedido.reclamar) return 'text-bg-danger';
      if (pedido.estado === 'entregado') return 'text-bg-success';
      if (pedido.estado === 'en_reparto' && pedido.llegaHoy) return 'text-bg-warning text-dark';
      if (pedido.estado === 'en_reparto') return 'text-bg-warning';
      return 'text-bg-primary';
    }

    function claseTarjeta(pedido) {
      if (pedido.reclamar) return 'border border-danger';
      if (pedido.llegaHoy) return 'pedido-llega-hoy';
      return '';
    }

    function etiquetaEntrega(pedido) {
      if (pedido.llegaHoy) return 'Llega hoy';
      if (!pedido.fechaEntrega) return 'En reparto · día de entrega pendiente';
      const fecha = new Date(`${pedido.fechaEntrega}T12:00:00`);
      if (Number.isNaN(fecha.getTime())) return `Entrega el ${pedido.fechaEntrega}`;
      const manana = new Date();
      manana.setHours(12, 0, 0, 0);
      manana.setDate(manana.getDate() + 1);
      const mismoDia = fecha.getFullYear() === manana.getFullYear()
        && fecha.getMonth() === manana.getMonth()
        && fecha.getDate() === manana.getDate();
      if (mismoDia) return 'Llega mañana';
      const texto = fecha.toLocaleDateString('es-ES', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
      });
      return `Entrega el ${texto}`;
    }

    function etiquetaAccion(accion) {
      return ETIQUETAS[accion] || accion;
    }

    function formatoFecha(value) {
      if (!value) return '';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return date.toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' });
    }

    onMounted(cargar);

    return {
      pedidos,
      logs,
      filtro,
      filtros,
      visibles,
      logsAbiertos,
      cargando,
      sincronizando,
      resolviendo,
      error,
      mensaje,
      connected,
      diasSinCerteza,
      sincronizar,
      toggleLogs,
      resolver,
      etiquetaEstado,
      claseEstado,
      claseTarjeta,
      etiquetaEntrega,
      etiquetaAccion,
      formatoFecha,
    };
  },
};
</script>

<style scoped>
.pedido-llega-hoy {
  border: 2px solid #e6a800 !important;
  background: linear-gradient(180deg, #fff8e1 0%, #fff 55%);
}

.entrega-dia {
  font-size: 1.15rem;
  font-weight: 650;
  margin: 0;
}

.entrega-hoy {
  color: #7a5a00;
}

.entrega-otro {
  color: #333;
}

.producto-pedido {
  color: #333;
  font-weight: 500;
}
</style>
